import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Alert,
  Stack,
  Typography,
  Box,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enAU } from 'date-fns/locale';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { format } from 'date-fns';
import apiService from '../services/api';
import SupaBuildingMultiPicker from './SupaBuildingMultiPicker';
import FormSection from './FormSection';

type FormMode = 'create' | 'edit';

interface NbnOutageFormDialogProps {
  open: boolean;
  mode: FormMode;
  outage?: any | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'planned', label: 'Planned' },
  { value: 'resolved', label: 'Resolved' },
];

const INCIDENT_TYPE_OPTIONS = [
  { value: 'unplanned', label: 'Unplanned' },
  { value: 'planned', label: 'Planned' },
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const NETWORK_OPTIONS = [
  { value: '', label: 'Unknown / Other' },
  { value: 'NBN Co', label: 'NBN Co' },
  { value: 'Vocus', label: 'Vocus' },
  { value: 'Supa Networks', label: 'Supa Networks' },
];

const DEFAULT_FORM_STATE = {
  incident_id: '',
  title: '',
  status: 'active',
  incident_type: 'unplanned',
  severity: 'medium',
  description: '',
  impact_description: '',
  resolution_description: '',
  affected_services: '',
  affected_areas: '',
  location_area: '',
  start_time: null as Date | null,
  estimated_resolution: null as Date | null,
  resolved_at: null as Date | null,
  source_system: 'Manual Entry',
  upstream_carrier: '',
  notes: '',
  impacted_building_keys: [] as string[],
};

const generateDescription = (state: typeof DEFAULT_FORM_STATE): string => {
  const rows: string[] = [];

  rows.push(`| **Network** | ${state.upstream_carrier || 'Unknown'} |`);
  if (state.location_area) rows.push(`| **Location** | ${state.location_area} |`);
  if (state.affected_areas) rows.push(`| **Areas Affected** | ${state.affected_areas} |`);
  if (state.start_time) rows.push(`| **Start Time** | ${format(state.start_time, 'dd/MM/yyyy hh:mm aa')} |`);
  if (state.estimated_resolution) rows.push(`| **Est. Resolution** | ${format(state.estimated_resolution, 'dd/MM/yyyy hh:mm aa')} |`);
  if (state.resolved_at) rows.push(`| **Resolved At** | ${format(state.resolved_at, 'dd/MM/yyyy hh:mm aa')} |`);
  rows.push(`| **Status** | ${STATUS_OPTIONS.find(o => o.value === state.status)?.label || state.status} |`);
  rows.push(`| **Severity** | ${SEVERITY_OPTIONS.find(o => o.value === state.severity)?.label || state.severity} |`);

  const table = `| | |\n|---|---|\n${rows.join('\n')}`;

  return `${table}\n\n---\n\n### Updates\n\n_No updates yet._`;
};

const NbnOutageFormDialog: React.FC<NbnOutageFormDialogProps> = ({
  open,
  mode,
  outage,
  onClose,
  onSuccess,
}) => {
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [idLoading, setIdLoading] = useState(false);

  const isEdit = mode === 'edit' && Boolean(outage);

  const fetchNextIncidentId = useCallback(async (network: string) => {
    setIdLoading(true);
    try {
      const id = await apiService.getNextIncidentId(network || undefined);
      setFormState((prev) => ({ ...prev, incident_id: id }));
    } catch {
      // fallback — leave existing value
    } finally {
      setIdLoading(false);
    }
  }, []);

  const initialState = useMemo(() => {
    if (!isEdit || !outage) {
      return { ...DEFAULT_FORM_STATE, start_time: new Date() };
    }
    return {
      incident_id: outage.incident_id || '',
      title: outage.title || '',
      status: outage.status || 'active',
      incident_type: outage.incident_type || (outage.planned ? 'planned' : 'unplanned'),
      severity: outage.severity || 'medium',
      description: outage.description || '',
      impact_description: outage.impact_description || '',
      resolution_description: outage.resolution_description || '',
      affected_services: Array.isArray(outage.affected_services) ? outage.affected_services.join(', ') : (outage.affected_services || ''),
      affected_areas: Array.isArray(outage.affected_areas) ? outage.affected_areas.join(', ') : (outage.affected_areas || ''),
      location_area: outage.location_area || '',
      start_time: outage.start_time ? new Date(outage.start_time) : null,
      estimated_resolution: outage.estimated_resolution ? new Date(outage.estimated_resolution) : null,
      resolved_at: outage.resolved_at ? new Date(outage.resolved_at) : null,
      source_system: outage.source_system || 'Manual Entry',
      upstream_carrier: outage.upstream_carrier || '',
      notes: outage.notes || '',
      impacted_building_keys: Array.isArray(outage.impacted_building_keys) ? outage.impacted_building_keys : [],
    };
  }, [isEdit, outage]);

  useEffect(() => {
    if (open) {
      setFormState(initialState);
      setError(null);
      if (!isEdit) {
        fetchNextIncidentId(initialState.upstream_carrier);
      }
    }
  }, [open, initialState, isEdit, fetchNextIncidentId]);

  const handleFieldChange = (field: string, value: any) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleNetworkChange = (network: string) => {
    handleFieldChange('upstream_carrier', network);
    if (!isEdit) {
      fetchNextIncidentId(network);
    }
  };

  const toList = (value: string) =>
    value.split(',').map((item) => item.trim()).filter(Boolean);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const payload: Record<string, any> = {
        incident_id: formState.incident_id?.trim() || undefined,
        title: formState.title.trim(),
        status: formState.status,
        incident_type: formState.incident_type,
        severity: formState.severity,
        description: formState.description?.trim() || undefined,
        impact_description: formState.impact_description?.trim() || undefined,
        resolution_description: formState.resolution_description?.trim() || undefined,
        location_area: formState.location_area?.trim() || undefined,
        source_system: formState.source_system?.trim() || 'Manual Entry',
        upstream_carrier: formState.upstream_carrier?.trim() || undefined,
        notes: formState.notes?.trim() || undefined,
        planned: formState.incident_type === 'planned',
        service_type: formState.upstream_carrier === 'Supa Networks' ? 'supa_networks' : undefined,
      };

      if (formState.upstream_carrier === 'Supa Networks') {
        payload.impacted_building_keys = formState.impacted_building_keys;
      }

      if (formState.affected_services) payload.affected_services = toList(formState.affected_services);
      if (formState.affected_areas) payload.affected_areas = toList(formState.affected_areas);
      if (formState.start_time) payload.start_time = formState.start_time.toISOString();
      if (formState.estimated_resolution) payload.estimated_resolution = formState.estimated_resolution.toISOString();
      if (formState.resolved_at) payload.resolved_at = formState.resolved_at.toISOString();

      if (isEdit && outage?.id) {
        await apiService.updateNbnOutage(outage.id, payload);
      } else {
        await apiService.createNbnOutage(payload);
      }

      onClose();
      onSuccess?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save outage');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
      <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle>{isEdit ? 'Edit Internet Outage' : 'Create Internet Outage'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <FormSection title="Identification">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Network"
                    value={formState.upstream_carrier}
                    onChange={(e) => handleNetworkChange(e.target.value)}
                    fullWidth
                    required
                  >
                    {NETWORK_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Incident ID"
                    value={formState.incident_id}
                    onChange={(e) => handleFieldChange('incident_id', e.target.value)}
                    fullWidth
                    required
                    disabled={idLoading}
                    helperText={idLoading ? 'Generating…' : isEdit ? undefined : 'Auto-generated — you may override'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Title"
                    value={formState.title}
                    onChange={(e) => handleFieldChange('title', e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection title="Location & Services">
              <Grid container spacing={2}>
                {formState.upstream_carrier === 'Supa Networks' && (
                  <Grid item xs={12}>
                    <SupaBuildingMultiPicker
                      value={formState.impacted_building_keys}
                      onChange={(keys) => handleFieldChange('impacted_building_keys', keys)}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Every customer with a service in a selected building will be linked to this outage and shown it in their customer portal.
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    label="Location Area"
                    fullWidth
                    value={formState.location_area}
                    onChange={(e) => handleFieldChange('location_area', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Affected Services (comma separated)"
                    fullWidth
                    value={formState.affected_services}
                    onChange={(e) => handleFieldChange('affected_services', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Affected Areas (comma separated)"
                    fullWidth
                    value={formState.affected_areas}
                    onChange={(e) => handleFieldChange('affected_areas', e.target.value)}
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection title="Timing">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <DateTimePicker
                    label="Start Time"
                    value={formState.start_time}
                    onChange={(value) => handleFieldChange('start_time', value)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DateTimePicker
                    label="Estimated Resolution"
                    value={formState.estimated_resolution}
                    onChange={(value) => handleFieldChange('estimated_resolution', value)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DateTimePicker
                    label="Resolved At"
                    value={formState.resolved_at}
                    onChange={(value) => handleFieldChange('resolved_at', value)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection title="Classification">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Status"
                    value={formState.status}
                    onChange={(e) => handleFieldChange('status', e.target.value)}
                    fullWidth
                    required
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Incident Type"
                    value={formState.incident_type}
                    onChange={(e) => handleFieldChange('incident_type', e.target.value)}
                    fullWidth
                  >
                    {INCIDENT_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Severity"
                    value={formState.severity}
                    onChange={(e) => handleFieldChange('severity', e.target.value)}
                    fullWidth
                  >
                    {SEVERITY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </FormSection>

            <FormSection title="Description">
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" mb={0.5}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleFieldChange('description', generateDescription(formState))}
                      sx={{ fontSize: '0.72rem' }}
                    >
                      Generate from fields
                    </Button>
                  </Box>
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    minRows={6}
                    value={formState.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    helperText="Click 'Generate from fields' to populate, then append update entries below the Updates heading"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Customer Impact Description"
                    fullWidth
                    multiline
                    minRows={3}
                    value={formState.impact_description}
                    onChange={(e) => handleFieldChange('impact_description', e.target.value)}
                    helperText="Markdown supported"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Resolution Description"
                    fullWidth
                    multiline
                    minRows={3}
                    value={formState.resolution_description}
                    onChange={(e) => handleFieldChange('resolution_description', e.target.value)}
                    helperText="Markdown supported"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection title="Additional">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Source System"
                    fullWidth
                    value={formState.source_system}
                    onChange={(e) => handleFieldChange('source_system', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Internal Notes"
                    fullWidth
                    multiline
                    minRows={2}
                    value={formState.notes}
                    onChange={(e) => handleFieldChange('notes', e.target.value)}
                  />
                </Grid>
              </Grid>
            </FormSection>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading || idLoading}>
            {isEdit ? 'Save Changes' : 'Create Outage'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default NbnOutageFormDialog;
